import { useState, useEffect, useRef } from 'react';
import { apiGet } from './apiClient';
import type { EventFromAsset } from './types';

export interface RelatedEventsResponse {
    series_slug: string | null;
    series_title: string | null;
    series_events: EventFromAsset[];
    similar_events: EventFromAsset[];
}

interface HookResult<T> {
    data: T | null;
    loading: boolean;
    error: Error | null;
}

export function useRelatedEvents(
    identifier: string | null,
): HookResult<RelatedEventsResponse> {
    const [data, setData] = useState<RelatedEventsResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const callIdRef = useRef(0);

    useEffect(() => {
        if (!identifier) {
            setData(null);
            setLoading(false);
            setError(null);
            return;
        }

        const callId = ++callIdRef.current;
        const controller = new AbortController();
        setLoading(true);
        setError(null);

        apiGet<RelatedEventsResponse>(
            `/api/related-events/${encodeURIComponent(identifier)}`,
            controller.signal,
        )
            .then(res => {
                if (callId !== callIdRef.current) return;
                setData(res);
            })
            .catch(err => {
                if (err?.name === 'AbortError' || callId !== callIdRef.current)
                    return;
                console.error('related-events failed:', err);
                setError(err);
                setData(null);
            })
            .finally(() => {
                if (callId !== callIdRef.current) return;
                setLoading(false);
            });

        return () => controller.abort();
    }, [identifier]);

    return { data, loading, error };
}